import React, { useEffect, useState } from "react";
import Styles from "./ContractDetails.module.css";
import { useDispatch, useSelector } from "react-redux";
import { convertDate, formatDateType } from "../../../../commons/FormatDate";
import { useNavigate } from "react-router-dom";
import ChildTransaction from "../../../Modals/Payment/ChildTransaction/ChildTransaction";
import { walletChildTransaction } from "../../../../Services/Payments/PaymentApi";
import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import {
  walletContract,
  walletTransaction,
} from "../../../../Redux/Slices/paymentSlice";
import { ArrowOutward } from "@mui/icons-material";

// ── Helpers ───────────────────────────────────────────────────────────

const toTitleCase = (str) =>
  str
    ?.split("_")
    ?.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    ?.join(" ") || "-";

// ── Sub-components ────────────────────────────────────────────────────

const TableHeading = ({ title, onViewAll }) => (
  <div className={Styles.tableHeadingContainer}>
    <div className={Styles.tableHeaderText}>{title}</div>
    <div className={Styles.viewAll} onClick={onViewAll}>
      <span>View All</span>
      <ArrowOutward style={{ fontSize: "14px", marginTop: "-15px" }} />
    </div>
  </div>
);

const ColHeaders = ({ cols }) => (
  <div className={Styles.tableHeaderParent}>
    <div className={Styles.tableListHeader}>
      {cols.map(({ label, width }) => (
        <div key={label} style={{ width }} title={label}>
          {label}
        </div>
      ))}
    </div>
    <div style={{ width: "8px" }} />
  </div>
);

const StatusBadge = ({ label, styleClass }) => (
  <span className={`${Styles.statusBadge} ${styleClass}`}>{label}</span>
);

const DeliveryCell = ({ delivered, scheduled }) => {
  const pct =
    scheduled > 0 ? Math.min(100, ((delivered || 0) / scheduled) * 100) : 0;
  return (
    <div
      className={Styles.deliveryCell}
      title={`${(delivered || 0).toFixed(2)} MWH`}
    >
      <div className={Styles.progressBarTrack}>
        <div className={Styles.progressBarFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={Styles.deliveryValue}>
        {(delivered || 0).toFixed(2)} MWH
      </span>
    </div>
  );
};

// ── Column definitions ────────────────────────────────────────────────

const CONTRACT_COLS = [
  { label: "Contract ID", width: "15%" },
  { label: "Self Listing", width: "5%" },
  { label: "Counter Party", width: "5%" },
  { label: "Transaction Date", width: "10%" },
  { label: "Total Contract Value", width: "10%" },
  { label: "Amount Blocked", width: "10%" },
  { label: "Total Contract Schedule", width: "10%" },
  { label: "Actual Delivery", width: "10%" },
  { label: "Status", width: "5%" },
  { label: "Details", width: "5%" },
];

const TRANSACTION_COLS = [
  { label: "Self Listing", width: "10%" },
  { label: "Counter Listing", width: "10%" },
  { label: "Counterparty Name", width: "15%" },
  { label: "Contract ID", width: "15%" },
  { label: "Date / Time", width: "15%" },
  { label: "Transaction Amount", width: "15%" },
  { label: "Type", width: "10%" },
  { label: "Status", width: "10%" },
];

// ── Main component ────────────────────────────────────────────────────

const ContractDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const role = useSelector((state) =>
    state?.persistedReducer?.user?.activeRole?.toLowerCase(),
  );
  const data = useSelector((state) => state?.paymentSlice);
  const wallet = useSelector(
    (state) => state?.paymentSlice?.walletAccountActive,
  );

  const contracts = data?.walletContract;
  const transaction = data?.walletTransaction;

  const [childTransaction, setChildTransaction] = useState([]);
  const [parentData, setParentData] = useState({});

  useEffect(() => {
    dispatch(walletTransaction({ page: 0, size: 10 }));
    if (wallet?.id) {
      dispatch(walletContract({ id: wallet?.id, page: 0, size: 10 }));
    }
  }, [dispatch, wallet?.id]);

  const handleChildTransaction = async (ele) => {
    setParentData(ele);
    const res = await walletChildTransaction(ele?.id);
    if (res?.data?.code === 200) {
      setChildTransaction(res?.data?.data || []);
    } else {
      ErrorMessage(res?.data?.error || "Something went wrong");
    }
  };

  const getSelfListing = (ele) =>
    (role === "consumer"
      ? ele?.name?.split("-")?.[0]?.includes("C")
        ? ele?.name?.split("-")?.[0]
        : ele?.name?.split("-")?.[1]
      : ele?.name?.split("-")?.[1]?.includes("G")
        ? ele?.name?.split("-")?.[1]
        : ele?.name?.split("-")?.[0]
    )?.split("_")?.[0] || "-";

  const getCounterParty = (ele) => {
    if (role === "consumer") return ele?.generatorName || "-";
    if (role === "generator") return ele?.consumerName || "-";
    return `${ele?.consumerName}-${ele?.generatorName}`;
  };

  const getContractStatus = (status) => {
    if (status === "COMPLETED")
      return { label: "Completed", cls: Styles.statusCompleted };
    if (status === "PENDING")
      return { label: "Ongoing", cls: Styles.statusOngoing };
    return { label: "Rejected", cls: Styles.statusRejected };
  };

  const getTypeStyle = (paymentType) => {
    if (paymentType?.includes("PAYIN")) return Styles.typePayin;
    if (paymentType?.includes("PAYOUT")) return Styles.typePayout;
    return Styles.typeDefault;
  };

  const getActivityStyle = (activity) =>
    activity === "CREDIT" || activity === "RELEASED"
      ? Styles.activityCredit
      : Styles.activityDebit;

  return (
    <>
      <ChildTransaction
        open={childTransaction?.length > 0}
        handleClose={() => setChildTransaction([])}
        data={childTransaction}
        parentData={parentData}
      />

      <div className={Styles.contractTransactionMainCont}>
        {/* ── Contract Details table ─────────────────────────────── */}
        <div className={Styles.tableMainContainer}>
          <TableHeading
            title="Contract Details"
            onViewAll={() => navigate("/wallet_contract")}
          />
          <ColHeaders cols={CONTRACT_COLS} />
          <div className={`scroll_bar_y ${Styles.displaytableCont}`}>
            {contracts?.map((ele) => {
              const { label: statusLabel, cls: statusCls } = getContractStatus(
                ele?.status,
              );
              return (
                <div
                  key={ele?.id || ele?.bilateralContractUniqueId}
                  className={Styles.tableListDetails}
                >
                  <div
                    style={{ width: "15%" }}
                    title={ele?.bilateralContractUniqueId || "-"}
                  >
                    {ele?.bilateralContractUniqueId || "-"}
                  </div>
                  <div style={{ width: "5%" }} title={getSelfListing(ele)}>
                    {getSelfListing(ele)}
                  </div>
                  <div style={{ width: "5%" }} title={getCounterParty(ele)}>
                    {getCounterParty(ele)}
                  </div>
                  <div
                    style={{ width: "10%" }}
                    title={`${formatDateType(ele?.startDate)} to ${formatDateType(ele?.endDate)}`}
                  >
                    {formatDateType(ele?.startDate)} to{" "}
                    {formatDateType(ele?.endDate)}
                  </div>
                  <div
                    style={{ width: "10%" }}
                    title={ele?.scheduledAmount?.toFixed(2)}
                  >
                    {ele?.scheduledAmount?.toFixed(2)}
                  </div>
                  <div
                    style={{ width: "10%" }}
                    title={(ele?.amountBlocked || 0).toFixed(2)}
                  >
                    {(ele?.amountBlocked || 0).toFixed(2)}
                  </div>
                  <div
                    style={{ width: "10%" }}
                    title={(ele?.scheduledVolume || 0).toFixed(2)}
                  >
                    {(ele?.scheduledVolume || 0).toFixed(2)}
                  </div>
                  <DeliveryCell
                    delivered={ele?.deliveredVolume}
                    scheduled={ele?.scheduledVolume}
                  />
                  <div style={{ width: "5%" }} className={Styles.statusCell}>
                    <StatusBadge label={statusLabel} styleClass={statusCls} />
                  </div>
                  <div style={{ width: "5%" }}>
                    <div
                      className={Styles.viewButton}
                      onClick={() => handleChildTransaction(ele)}
                    >
                      View
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Transaction Details table ──────────────────────────── */}
        <div className={Styles.tableMainContainer}>
          <TableHeading
            title="Transaction Details"
            onViewAll={() => navigate("/wallet_transaction")}
          />
          <ColHeaders cols={TRANSACTION_COLS} />
          <div className={`scroll_bar_y ${Styles.displaytableCont}`}>
            {transaction?.map((ele) => (
              <div key={ele?.id} className={Styles.tableListDetails}>
                <div
                  style={{ width: "10%" }}
                  title={ele?.listingId?.split("_")?.[0] || "-"}
                >
                  {ele?.listingId?.split("_")?.[0] || "-"}
                </div>
                <div
                  style={{ width: "10%" }}
                  title={ele?.counterPartyListingId?.split("_")?.[0] || "-"}
                >
                  {ele?.counterPartyListingId?.split("_")?.[0] || "-"}
                </div>
                <div
                  style={{ width: "15%" }}
                  title={
                    (ele?.counterPartyName === "UNKNOWN"
                      ? ""
                      : ele?.counterPartyName) || "Not Generated"
                  }
                >
                  {(ele?.counterPartyName === "UNKNOWN"
                    ? ""
                    : ele?.counterPartyName) || "Not Generated"}
                </div>
                <div
                  style={{ width: "15%" }}
                  title={ele?.bilateralContractUniqueId || "Not Generated"}
                >
                  {ele?.bilateralContractUniqueId || "Not Generated"}
                </div>
                <div
                  style={{ width: "15%" }}
                  title={`${convertDate(ele?.dateTime?.split(" ")?.[0])} | ${ele?.dateTime?.split(" ")?.[1]}`}
                >
                  {convertDate(ele?.dateTime?.split(" ")?.[0])} |{" "}
                  {ele?.dateTime?.split(" ")?.[1]}
                </div>
                <div
                  style={{ width: "15%" }}
                  title={`₹ ${ele?.amount?.toFixed(2)}`}
                >
                  ₹ {ele?.amount?.toFixed(2)}
                </div>
                <div style={{ width: "10%" }} className={Styles.statusCell}>
                  <StatusBadge
                    label={toTitleCase(ele?.paymentType)}
                    styleClass={getTypeStyle(ele?.paymentType)}
                  />
                </div>
                <div style={{ width: "10%" }} className={Styles.statusCell}>
                  <StatusBadge
                    label={toTitleCase(ele?.activity)}
                    styleClass={getActivityStyle(ele?.activity)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContractDetails;
